import React, { ReactElement } from 'react';
import { LocationDetailData, Tab } from '../../types';
import { DashboardsTab } from '../../../../../styles/shared-styles';
import { BreakdownSelection } from '../breakdownSelection';
import LocationDetailAttributesTile from './LocationDetailAttributesTile';
import LocationDetailBreadcrumb from './LocationDetailBreadcrumb';
import LocationDetailContactsTile from './LocationDetailContactsTile';
import LocationDetailFilesTile from './LocationDetailFilesTile';
import LocationDetailLinksTile from './LocationDetailLinksTile';
import LocationDetailTasksTile from './LocationDetailTasksTile';
import './LocationDetailTile.scss';

export const DEFAULT_LOCATION_DETAIL_TAB_ID = 1;

// Appended rather than slotted into the 1-6 run: the ids are the values the parent remembers
// across opens, so renumbering the existing tabs would silently move a remembered selection
// to a different tab.
export const TASKS_LOCATION_DETAIL_TAB_ID = 7;

interface LocationDetailTileProps {
    location: LocationDetailData;
    projectIds?: number[];
    /**
     * Which sub-tab is open. Owned by the parent, not by this tile: the tile is unmounted
     * whenever the selected location's details are refetched, and there is a second copy of
     * it in the mobile drawer — local state would reset on every location change and the two
     * copies would disagree.
     */
    activeTabId: number;
    onTabChange: (tabId: number) => void;
    /**
     * Set only when the sidebar was opened by drilling into a breakdown row. It swaps the
     * header for a breadcrumb back to the location, and is what a later story will use to
     * scope the Tasks tab's query.
     */
    breakdownSelection?: BreakdownSelection | null;
    /** Leaves the drill-down: closes the sidebar and leaves the breakdown panel open. */
    onBack?: () => void;
}

const LocationDetailTile = ({
    location,
    projectIds = [],
    activeTabId,
    onTabChange,
    breakdownSelection = null,
    onBack,
}: LocationDetailTileProps): ReactElement => {

    const detailSubTabs: Array<Tab> = [
        {
            id: 1,
            label: 'Attributes',
            component: <LocationDetailAttributesTile location={location} />
        },
        {
            id: TASKS_LOCATION_DETAIL_TAB_ID,
            label: 'Tasks',
            component: <LocationDetailTasksTile groupName={breakdownSelection?.groupName} />
        },
        {
            id: 2,
            label: 'Notes',
            component: <></>
        },
        {
            id: 3,
            label: 'Comments',
            component: <></>
        },
        {
            id: 4,
            label: 'Contacts',
            component: <LocationDetailContactsTile location={location} />
        },
        {
            id: 5,
            label: 'Files',
            component: <LocationDetailFilesTile location={location} projectIds={projectIds} />
        },
        {
            id: 6,
            label: 'Links',
            component: <LocationDetailLinksTile location={location} projectIds={projectIds} />
        },
    ];

    // Falls back to the first tab so a stale id — from a tab that no longer exists — cannot
    // leave the panel with a header and no body.
    const activeTab: Tab = detailSubTabs.find((tab) => tab.id === activeTabId) ?? detailSubTabs[0];

    // Only the Tasks tab is scoped to the group that was drilled into; every other tab shows
    // location-wide data. The title labels whatever the body is rendering, so it follows the
    // tab rather than the drill-down — otherwise Contacts, Files and Links sit under a group's
    // name while showing nothing of that group.
    const isGroupScopedTab = activeTab.id === TASKS_LOCATION_DETAIL_TAB_ID;
    const groupTitle = isGroupScopedTab ? breakdownSelection?.groupName : undefined;
    const title = groupTitle ?? location.name;

    return (
        <div className='location-detail-tile'>
            {breakdownSelection && (
                <LocationDetailBreadcrumb
                    selection={breakdownSelection}
                    current={isGroupScopedTab ? 'group' : 'location'}
                    onBack={onBack}
                />
            )}

            <div className='location-detail-tile-header'>
                <div className='location-detail-tile-header-title' title={title}>
                    {title}
                </div>
                <div className='location-detail-tile-header-actions'>
                    <i className="fa-regular fa-pen"></i>
                    {breakdownSelection ? (
                        // Not plain 'Close': on mobile this sits inside the detail drawer,
                        // whose own close button is a second X a few pixels away that only
                        // closes the drawer. Two controls labelled the same doing different
                        // things is ambiguous for screen readers and for by-label queries.
                        <i
                            role='button'
                            tabIndex={0}
                            aria-label='Close breakdown'
                            title='Close breakdown'
                            className='fa-solid fa-xmark'
                            onClick={onBack}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onBack?.();
                                }
                            }}
                        />
                    ) : (
                        <i className="fa-regular fa-ellipsis"></i>
                    )}
                </div>
            </div>

            <div className='row g-0 align-items-end'>
                <div className='col-12'>
                    <DashboardsTab className='nav nav-tabs'>
                        {detailSubTabs.map((tab) => (
                            <li key={tab.id} className='nav-item'>
                                <button
                                    className={`nav-link ${activeTab?.id === tab.id ? 'active' : ''}`}
                                    onClick={() => onTabChange(tab.id)}
                                    type='button'>
                                    <i className={`fa-regular ${tab.icon}`} />
                                    <span>{tab.label}</span>
                                </button>
                            </li>
                        ))}
                    </DashboardsTab>
                </div>

            </div>

            <div className='row w-100'>
                <div className='col-12'>{activeTab.component}</div>
            </div>
        </div>
    );
};

export default LocationDetailTile;
